import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '../config/api';

const VerifyClientEmail = () => {
  const { tenantSlug } = useParams();
  const [params] = useSearchParams();
  const email = params.get('email') || '';
  const token = params.get('token') || '';

  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const [resending, setResending] = useState(false);
  const [resendNotice, setResendNotice] = useState('');

  const loginPath = tenantSlug ? `/${tenantSlug}/login` : '/login';

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!email || !token) {
        if (alive) {
          setStatus('error');
          setMessage('This verification link is missing required information. Please use the link from your email.');
        }
        return;
      }
      try {
        const res = await axios.post(apiUrl('/api/auth/verify-client-email'), {
          email,
          token,
          tenantSlug: tenantSlug || null,
        });
        if (!alive) return;
        setStatus('success');
        setMessage(res.data?.message || 'Email verified. An administrator must approve your account before you can sign in.');
      } catch (err) {
        if (!alive) return;
        setStatus('error');
        setMessage(err.response?.data?.error || 'Verification failed. The link may have expired.');
      }
    })();
    return () => { alive = false; };
  }, [email, token, tenantSlug]);

  const resend = async () => {
    if (!email) return;
    setResending(true);
    setResendNotice('');
    try {
      await axios.post(apiUrl('/api/auth/resend-client-verification'), {
        email,
        tenantSlug: tenantSlug || null,
      });
      setResendNotice('If this email is awaiting verification, a new link has been sent.');
    } catch {
      setResendNotice('Could not request a new link. Please try again later.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 p-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden">
        <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />
        <div className="px-6 py-8 sm:px-8 sm:py-10 text-center">
          <h1 className="text-2xl font-extrabold text-blue-800 dark:text-blue-300 mb-3">Email verification</h1>

          {status === 'verifying' && (
            <p className="text-sm text-gray-600 dark:text-slate-300">Verifying your email…</p>
          )}

          {status === 'success' && (
            <>
              <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center text-2xl">✅</div>
              <p className="text-sm text-gray-700 dark:text-slate-200 mb-5">{message}</p>
              <Link
                to={loginPath}
                className="inline-block px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
              >
                Go to sign in
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center text-2xl">⚠️</div>
              <p className="text-sm text-red-700 dark:text-red-300 mb-5">{message}</p>
              {email && (
                <>
                  <button
                    type="button"
                    onClick={resend}
                    disabled={resending}
                    className="px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-60"
                  >
                    {resending ? 'Sending…' : 'Resend verification link'}
                  </button>
                  {resendNotice && (
                    <p className="mt-3 text-xs text-gray-600 dark:text-slate-400">{resendNotice}</p>
                  )}
                </>
              )}
              <div className="mt-5 text-sm">
                <Link to={loginPath} className="text-blue-700 dark:text-blue-300 hover:underline">Back to sign in</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyClientEmail;
